import toast from 'react-hot-toast';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { useDynamicEntityCMSDataContext, useDynamicEntityStoreContext } from '../contexts';
import { COMPONENT_IDENTIFIER_KEYWORD, COMPONENT_LABEL_KEYWORD, PREVIEW_NO_LAYOUT_SOURCE, generateBlankProps } from '../lib';
import { convertSnakeCaseToReadable } from '../../utils/StringFormatters';

import TextInput from '../../inputs/form-inputs/TextInput';
import ComponentButton from './_component_selection/ComponentButton';

export default function ComponentSelectionPane({ toActivePaneAfterAdd = 1 }) {
  const searchInputRef = useRef(null);

  const { componentDefinitions, isTemplate } = useDynamicEntityCMSDataContext();

  const addContentAtIndex = useDynamicEntityStoreContext((state) => state.addContentAtIndex);
  const addContent = useDynamicEntityStoreContext((state) => state.addContent);
  const update = useDynamicEntityStoreContext((state) => state.update);

  const [searchValue, setSearchValue] = useState('');
  const [selectedComponentOrigin, setSelectedComponentOrigin] = useState('');
  const [selectedComponentCategory, setSelectedComponentCategory] = useState('');
  const [selected, setSelected] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [hasNoLayoutPreview, setHasNoLayoutPreview] = useState(false);

  const componentCategories = useMemo(
    () =>
      [...new Set(componentDefinitions.map((c) => c.component_category))].filter(Boolean).sort((a, b) => {
        return a.localeCompare(b);
      }),
    [componentDefinitions],
  );

  const componentOrigins = useMemo(
    () =>
      [...new Set(componentDefinitions.map((c) => c.component_origin))].filter(Boolean).sort((a, b) => {
        return a.localeCompare(b);
      }),
    [componentDefinitions],
  );

  const filteredComponentDefinitions = useMemo(
    () =>
      componentDefinitions
        .filter((c) => selectedComponentOrigin === '' || c.component_origin === selectedComponentOrigin)
        .filter((c) => selectedComponentCategory === '' || c.component_category === selectedComponentCategory)
        .filter((c) => c[COMPONENT_IDENTIFIER_KEYWORD].toLowerCase().includes(searchValue) || c[COMPONENT_LABEL_KEYWORD].toLowerCase().includes(searchValue))
        .sort((a, b) => {
          const aLabel = a[COMPONENT_LABEL_KEYWORD]?.toLowerCase() || '';
          const bLabel = b[COMPONENT_LABEL_KEYWORD]?.toLowerCase() || '';
          return aLabel.localeCompare(bLabel);
        }),
    [componentDefinitions, selectedComponentOrigin, selectedComponentCategory, searchValue],
  );

  const handleAddComponent = useCallback(() => {
    if (!selected) return;
    try {
      const props = selected.propSchema ? generateBlankProps(selected.propSchema) : {};

      const { propSchema, component_category, label, ...rest } = selected;

      const newComponent = {
        id: crypto.randomUUID(), // We must always have an ID generated here, make sure the crypto API is suitable
        ...(Object.keys(props).length > 0 && { props }), // Only add props if they actually exist
        ...rest, // Add the rest of the properties which just includes: 'component' for now
      };

      if (addContentAtIndex === undefined) {
        addContent(newComponent);
      } else {
        addContent(newComponent, addContentAtIndex);
      }

      update({ activePane: toActivePaneAfterAdd });
    } catch (error) {
      toast.error(`Unable to add component: ${error.message}`);
    }
  }, [selected, addContentAtIndex, addContent, update, toActivePaneAfterAdd]);

  const handleSelectComponent = useCallback(
    (componentName) => {
      const component = componentDefinitions.find((c) => c[COMPONENT_IDENTIFIER_KEYWORD] === componentName);
      setSelected(component);
    },
    [componentDefinitions],
  );

  const handleUnselectComponent = useCallback(() => {
    setSelected(null);
  }, []);

  const handleSearchComponent = useCallback((event) => {
    const searchValue = event.target.value.toLowerCase();

    setSearchValue(searchValue);
  }, []);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    fetch(`/${PREVIEW_NO_LAYOUT_SOURCE}`, { method: 'HEAD' })
      .then((response) => {
        if (!response.ok) {
          setHasNoLayoutPreview(false);
        } else {
          setHasNoLayoutPreview(true);
        }
      })
      .catch(() => {
        setHasNoLayoutPreview(false);
      });

    return () => {
      setSelected(null);
    };
  }, []);

  return (
    <div className="d-flex flex-column gap-3 h-100">
      <div className="d-flex flex-column">
        <h3 className="mb-1">Add a new component</h3>
        <p className="text-muted mb-0">Select a component you wish to add as content.</p>
      </div>
      <TextInput ref={searchInputRef} label="Search" placeholder="Search for a component..." onChange={handleSearchComponent} />
      <div className="position-relative">
        <div className="dp-component-selection-filter-container">
          <div className="filter-text">
            <span>{convertSnakeCaseToReadable(selectedComponentCategory || 'All Components')}</span>
            <span>|</span>
            <span>{convertSnakeCaseToReadable(selectedComponentOrigin || 'All Origins')}</span>
          </div>
          <button type="button" className={`filter-button ${showMenu ? 'active' : ''}`} onClick={() => setShowMenu(!showMenu)}>
            <i className="fe fe-filter me-2"></i>
            Filter
          </button>
        </div>
        {showMenu && (
          <div className="dp-component-selection-filter-menu">
            <div className="component-filters">
              <h2 className="text-muted mb-0">Categories</h2>
              <div className="buttons">
                <button
                  className={`button ${selectedComponentCategory === '' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedComponentCategory('');
                    setShowMenu(false);
                  }}
                >
                  All Components
                </button>
                {componentCategories && componentCategories.length
                  ? componentCategories.map((category, index) => {
                      if (!isTemplate && category?.toString()?.startsWith('for_template_')) {
                        return null; // Skip the layout category if not a template
                      }

                      return (
                        <button
                          key={index}
                          className={`button ${selectedComponentCategory === category ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedComponentCategory(category);
                            setShowMenu(false);
                          }}
                        >
                          {convertSnakeCaseToReadable(category)}
                        </button>
                      );
                    })
                  : null}
              </div>
            </div>

            <div className="component-filters mt-3">
              <h2 className="text-muted mb-0">Origins</h2>
              <div className="buttons">
                <button
                  className={`button ${selectedComponentOrigin === '' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedComponentOrigin('');
                    setShowMenu(false);
                  }}
                >
                  All Origins
                </button>
                {componentOrigins && componentOrigins.length
                  ? componentOrigins.map((origin, index) => {
                      if (!isTemplate && origin?.toString()?.startsWith('for_template_')) {
                        return null; // Skip the layout category if not a template
                      }

                      return (
                        <button
                          key={index}
                          className={`button ${selectedComponentOrigin === origin ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedComponentOrigin(origin);
                            setShowMenu(false);
                          }}
                        >
                          {convertSnakeCaseToReadable(origin)}
                        </button>
                      );
                    })
                  : null}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="dp-component-selection-list">
        {!filteredComponentDefinitions.length ? (
          <div className="d-flex flex-column align-items-center justify-content-center p-5 text-muted">No components found</div>
        ) : (
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                itemData={filteredComponentDefinitions}
                itemCount={filteredComponentDefinitions.length}
                itemSize={hasNoLayoutPreview ? 400 : 180}
              >
                {({ data, index, style }) => {
                  const c = data[index];
                  const isSelected = selected && selected[COMPONENT_IDENTIFIER_KEYWORD] === c[COMPONENT_IDENTIFIER_KEYWORD];

                  return (
                    <div style={style}>
                      <ComponentButton
                        {...{
                          c,
                          isSelected,
                          handleSelectComponent: () => handleSelectComponent(c[COMPONENT_IDENTIFIER_KEYWORD]),
                          handleUnselectComponent,
                          handleAddComponent,
                          hasNoLayoutPreview,
                        }}
                      />
                    </div>
                  );
                }}
              </List>
            )}
          </AutoSizer>
        )}
      </div>
    </div>
  );
}
